import React, { useMemo } from "react";
import styles from "./styles.module.scss";
import { useSelector, shallowEqual } from "react-redux";
import { RootState } from "typedefs";
import { TimeZoneState } from "app/reducers/scheduling/timeZone";
import { TimeZoneModel } from "app/models/scheduling/TimeZoneModel";
import useFacilityLessonRates from "../hooks/useFacilityLessonRates";
import usePackageCredits from "../hooks/usePackageCredits";
import { Loading } from "app/components/Wrappers";
import { hasFacilityBanner } from "app/components/PublicProfiles/Facility/facilityBranding";
import FacilityBooking from "./_components/FacilityBooking";
import CoachDetail from "../CoachDetail";

const AllCoachesPublicSession = ({ bookLessonData }) => {
  const {
    id: facilityId,
    timeZone: facilityTimeZone,
    featureFlags,
    public_cover_url: publicCoverUrl,
  } = useSelector((state: RootState) => state.bookSession.facility) as any;
  // The facility's logo, name and banner belong to the public header strip
  // (PublicWrapper), which reads them from the same slice — this page draws no
  // facility identity of its own.

  // Only a logged-in athlete viewing the public calendar has package credits
  const { self: user } = useSelector(
    (state: RootState) => state.user,
    shallowEqual,
  );
  const isAthlete = user?.kind === "athlete" || user?.kind === "other";
  const isLoggedInAthlete = Boolean(user?.id) && isAthlete;
  // CFTB-3318: a head booking for a managed dependent sees that member's
  // package credits; null (self-booking) keeps the head's own bundles.
  const participantAthleteId = useSelector(
    (state: RootState) => state.bookSession.participantAthleteId,
  );
  const { credits: packageCredits, loading: packageCreditsLoading } =
    usePackageCredits(isLoggedInAthlete, participantAthleteId);

  const facilityBookingEnabled = featureFlags?.facilityLevelBookingOptions === true;

  // Which masthead is above this page — see `showTitleRow` below.
  const overBanner = hasFacilityBanner(publicCoverUrl);

  const { rates: facilityRates, loading: facilityRatesLoading } = useFacilityLessonRates(
    facilityId,
    facilityBookingEnabled,
    true
  );

  const coaches = bookLessonData?.coaches?.[facilityId] ?? [];

  const displayCoaches = useMemo(() => {
    if (!facilityBookingEnabled || facilityRates.length === 0) {
      return coaches;
    }
    return coaches.map(coach => ({
      ...coach,
      lessonRates: facilityRates,
      currencySymbol: '$'
    }));
  }, [coaches, facilityBookingEnabled, facilityRates]);

  const singleCoach = displayCoaches.length === 1;
  const timeZones = useSelector(
    (state: RootState): TimeZoneState => state.schedulingTimeZones,
  );
  const timezone =
    timeZones.data.find(
      (item: TimeZoneModel) => item.value === facilityTimeZone,
    )?.display || "";

  if ((facilityBookingEnabled && facilityRatesLoading) || packageCreditsLoading) {
    return (
      <div className={styles.container}>
        <Loading isLoading loadType="spinner" />
      </div>
    );
  }

  // CN-2652: a multi-coach facility picks a coach first and sees that coach's
  // booking options on their own page, instead of every coach's options side by
  // side.
  if (!singleCoach) {
    return (
      <FacilityBooking
        coaches={displayCoaches}
        timezone={timezone}
        packageCredits={packageCredits}
        /*
          CFTB-3433 dropped this row because the facility names itself in the strip
          above — which it does, beside a logo. Over a banner it does not: the
          picture carries the facility and the page needs to say what it is, which
          is why 526:57411, 528:58537 and 528:61284 all head it "Book a Session".
          The row is the heading alone on a public page (see `FacilityBooking`).
        */
        showTitleRow={overBanner}
        showTopDivider />
    );
  }

  /**
   * A facility with one coach has no coach to pick, so its public calendar *is*
   * that coach's page — frame 526:35466, the same page their own public link
   * opens.
   *
   * This was the last public surface still on the old layout: a booking-options
   * panel that dropped the athlete straight onto the slot list, which is the
   * redirect the new public flow rules out. Booking continues on the coach's own
   * calendar URL rather than `/public/facility_slots`, so a single-coach facility
   * and a coach's link share one flow from here on.
   */
  const soleCoach = displayCoaches[0];

  return (
    <CoachDetail
      coachExternalId={soleCoach?.externalId}
      // The facility payload carries the coach's rates, and this page must not
      // depend on `landing_page_coach` for them — that endpoint answers 403 for a
      // coach who has a public calendar but no landing page. Facility-level
      // booking options are already merged in above.
      lessonRates={soleCoach?.lessonRates}
      showFacilityRow={false}
      isPublicPage
      bookRoute={(rate) =>
        `/public/availability?coach=${soleCoach?.calendarUuid}&sessionId=${rate.id}`
      }
    />
  );
};

export default AllCoachesPublicSession;
